import React, { useEffect, useState } from "react";
import { withStyles } from "@material-ui/core/styles";
import { useStyles } from "../../../assets/jss/Dialog";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import CustomDialog from "../../CustomComponents/CustomDialog/CustomDialog";
import CustomDialogContent from "../../CustomComponents/CustomDialogContent/CustomDialogContent";
import CustomDialogAction from "../../CustomComponents/CustomDialogAction/CustomDialogAction";
import CustomButton from "../../CustomComponents/CustomButton/CustomButton";
import { useTranslation } from "react-i18next";
import { GetUserLicenseLogsAPI, SendEConsentAPI, ValidateLicenseAPI } from "../../../services/LicenseCheckService";
import CustomCircularProgress from "../../CustomComponents/CustomCircularProgress/CustomCircularProgress";
import DateTime from "../../DateTime/DateTime";
import { LicenseCheckRequestType, LICENSE_OPERATION_STATUS_ENUM } from "../../../config/app-globals.enum";
import { getMinResendEconsentDays, getMinResendValidationDays } from "../../../services/LocalStorageService";

const DialogContent = withStyles((theme) => ({}))(CustomDialogContent);
const DialogActions = withStyles((theme) => ({}))(CustomDialogAction);

export default function ValidateLicenseConfirmDialog(props) {
    const { t } = useTranslation();
    const classes = useStyles();
    const [lastEconsentRecord, setLastEconsentRecord] = useState(null);
    const [lastValidationRecord, setLastValidationRecord] = useState(null);
    const [isApiLoading, setIsApiLoading] = useState(false);
    const [isButtonDisable, setIsButtonDisable] = useState(true);
    const [isGetLicenseLogsApiLoading, setIsGetLicenseLogsApiLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState<{ type: string, message: string, tillDate: Date }>(null);
    const [reloadParent, setReloadParent] = useState(false);
    const [minResendEconsentDays] = useState(getMinResendEconsentDays());
    const [minResendValidationDays] = useState(getMinResendValidationDays());

    const handleClose = () => {
        if (reloadParent) {
            props.getLicenseData('READ');
        }
        props.setIsValidateConfirmDialogOpen(false);
    };

    const getLicenseValidationLogs = async (licenseDetailsId: string) => {
        setIsGetLicenseLogsApiLoading(true);
        const data = await GetUserLicenseLogsAPI(licenseDetailsId);
        if (data && (data.status.code === 0 || data.status.code === 200 || data.status.code === 201) && data.content) {
            const list: any[] = data.content;
            if (list.length) {
                const econcent = list.find(ec => ec.requestType === LicenseCheckRequestType.ECONSENT);
                if (econcent) {
                    setLastEconsentRecord(econcent);
                }
                const validate = list.find(ec => ec.requestType === LicenseCheckRequestType.DRIVING_LICENSE_CHECK);
                if (validate) {
                    setLastValidationRecord(validate);
                }
            }
            setIsGetLicenseLogsApiLoading(false);
        } else {
            setLastEconsentRecord(null);
            setLastValidationRecord(null);
            props.licenseApiStatusCodeFunc(data.status, null);
        }
        setIsButtonDisable(false);
    }

    useEffect(() => {
        if (props.selectedLicense !== undefined && props.selectedLicense !== null) {
            getLicenseValidationLogs(props.selectedLicense.id);
        }
    }, [props.licenseDetailsId]);


    const onClickValidateButton = async () => {
        try {
            if (isApiLoading) {
                return;
            }
            setStatusMessage(null);
            if (props.selectedLicense !== undefined && props.selectedLicense !== null) {
                setIsApiLoading(true);
                setIsButtonDisable(true);
                if (minResendValidationDays > 0 && lastValidationRecord && lastValidationRecord.createdOn) {
                    let currentDate = new Date(lastValidationRecord.createdOn);
                    currentDate.setDate(currentDate.getDate() + minResendValidationDays);
                    if ((new Date()).getTime() < currentDate.getTime()) {
                        setIsApiLoading(false);
                        setStatusMessage({
                            type: "error",
                            message: t("CAN_NOT_PERFORM_OPERATION"),
                            tillDate: currentDate
                        });
                        return;
                    }
                }
                const data = await ValidateLicenseAPI(props.selectedLicense.id);
                if (data && (data.status.code === 0 || data.status.code === 200 || data.status.code === 201) && data.content) {
                    setReloadParent(true);
                    setStatusMessage({
                        type: "success",
                        message: t("LC_VALIDATED_SUCCESSFULY"),
                        tillDate: null
                    })
                } else {
                    if (data && data.content && data.content.Code) {
                        if (data.content.Code === "324") {
                            setStatusMessage({
                                type: "error",
                                message: t("LC_EAPPROVAL_NOT_PROCESSED"),
                                tillDate: null
                            });
                        }
                    } else {
                        setStatusMessage({
                            type: "error",
                            message: t("SOMETHING_WENT_WRONG"),
                            tillDate: null
                        });
                    }
                    // props.licenseApiStatusCodeFunc(data.status, null);
                    // setIsButtonDisable(false);
                }
            }
        } catch (error) {
            props.licenseApiStatusCodeFunc({ code: 0 }, null);
        }
        setIsApiLoading(false);
    }

    const onClickEConsentButton = async () => {
        try {
            if (isApiLoading) {
                return;
            }
            if (props.selectedLicense !== undefined && props.selectedLicense !== null && [LICENSE_OPERATION_STATUS_ENUM.LICENSE_CREATED, LICENSE_OPERATION_STATUS_ENUM.CONSENT_SENT].includes(props.selectedLicense.licenseOperationStatus)) {
                setIsApiLoading(true);
                setIsButtonDisable(true);
                if (lastEconsentRecord && lastEconsentRecord.createdOn && minResendEconsentDays > 0) {
                    let currentDate = new Date(lastEconsentRecord.createdOn);
                    currentDate.setDate(currentDate.getDate() + minResendEconsentDays);
                    if ((new Date()).getTime() < currentDate.getTime()) {
                        setIsApiLoading(false);
                        setStatusMessage({
                            type: "error",
                            message: t("CAN_NOT_PERFORM_OPERATION"),
                            tillDate: currentDate
                        });
                        return;
                    }
                }
                const data = await SendEConsentAPI(props.selectedLicense.id);
                if (data && (data.status.code === 0 || data.status.code === 200 || data.status.code === 201) && data.content) {
                    setReloadParent(true);
                    props.licenseApiStatusCodeFunc(data.status, "ECONSENT_SENT");
                    handleClose();
                } else {
                    props.licenseApiStatusCodeFunc(data.status, null);
                    setIsButtonDisable(false);
                }
            }
        } catch (error) {
            props.licenseApiStatusCodeFunc({ code: 0 }, null);
        }
        setIsApiLoading(false);
    }

    return (
        <CustomDialog
            onClose={() => handleClose()}
            aria-labelledby="customized-dialog-title"
            open={true}
            className={classes.dialogWrapper}
        >
            <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={() => handleClose()}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent className={classes.dialogContentWrapper}>
                <Typography className={classes.dialogContentTitle}>
                    {props.econsentOrValidationDialog === "EC" && t("ARE_YOU_SURE_YOU_WANT_TO_SEND_ECONSENT")}
                    {props.econsentOrValidationDialog === "V" && t("ARE_YOU_SURE_YOU_WANT_TO_VALIDATE_THIS_LICENSE")}
                </Typography>
                <Typography component={'div'} style={{ minHeight: 40, display: "flex", alignItems: "center" }}>
                    {t("LAST_TIME_SENT_ECONSENT")}:
                    &nbsp;
                    {
                        isGetLicenseLogsApiLoading ?
                            <CustomCircularProgress style={{ marginLeft: 16 }} />
                            :
                            <b>
                                {
                                    lastEconsentRecord === null || lastEconsentRecord.createdOn === null ?
                                        t("NEVER") :
                                        <DateTime
                                            inputDate={lastEconsentRecord.createdOn}
                                            returnFormat="DD MMM, YYYY"
                                        />
                                }
                            </b>
                    }
                </Typography>
                <Typography component={'div'} style={{ minHeight: 40, display: "flex", alignItems: "center" }}>
                    {t("LAST_TIME_SENT_VALIDATION_REQUEST")}:
                    &nbsp;
                    {
                        isGetLicenseLogsApiLoading ?
                            <CustomCircularProgress style={{ marginLeft: 16 }} />
                            :
                            <b>
                                {
                                    lastValidationRecord === null || lastValidationRecord.createdOn === null ?
                                        t("NEVER") :
                                        <DateTime
                                            inputDate={lastValidationRecord.createdOn}
                                            returnFormat="DD MMM, YYYY"
                                        />
                                }
                            </b>
                    }
                </Typography>
                <br />
                {statusMessage && <Typography component={'div'} style={statusMessage.type === 'error' ? { color: "#d00000" } : statusMessage.type === 'success' ? { color: "#9fd356" } : {}}>
                    {t("STATUS")}:&nbsp; <b>{statusMessage.message} {statusMessage.tillDate && <DateTime inputDate={statusMessage.tillDate} returnFormat="DD MMM, YYYY" />}</b>
                </Typography>}
            </DialogContent>
            <DialogActions className={classes.dialogActionWrapper}>
                <CustomButton
                    autoFocus
                    onClick={() => handleClose()}
                    color="primary"
                    className={classes.noBg}
                >
                    {t("CANCEL")}
                </CustomButton>
                {props.econsentOrValidationDialog === "V" &&
                    <CustomButton
                        autoFocus
                        disabled={isGetLicenseLogsApiLoading || isApiLoading || isButtonDisable}
                        onClick={() => onClickValidateButton()}
                        variant="contained"
                        color="secondary"
                        defaultrounded="true"
                    >
                        {t("VALIDATE")}
                    </CustomButton>
                }
                {props.econsentOrValidationDialog === "EC" &&
                    <CustomButton
                        autoFocus
                        disabled={isGetLicenseLogsApiLoading || isApiLoading || isButtonDisable}
                        onClick={() => onClickEConsentButton()}
                        variant="contained"
                        color="secondary"
                        defaultrounded="true"
                    >
                        {t("SEND_ECONSENT")}
                    </CustomButton>
                }
                {isApiLoading && (
                    <CustomCircularProgress style={{ marginLeft: 8 }} />
                )}
            </DialogActions>
        </CustomDialog>
    );
}
